import { forwardRef } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@tsgecart/ui';

/**
 * Shared premium button primitive for the storefront redesign -- shadcn/ui's
 * `cva`-variant pattern, with Framer Motion's `whileHover`/`whileTap` for the
 * "smooth interactions" requirement instead of pure-CSS transitions. Scoped
 * to `features/storefront/components/ui/` deliberately: the admin dashboard
 * has its own separate primitive set (`features/admin/components/primitives/`)
 * and is explicitly out of scope for this redesign.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-brand text-brand-foreground shadow-sm hover:bg-brand/90',
        gradient:
          'bg-gradient-to-r from-brand via-brand to-brand-orange text-brand-foreground shadow-md hover:shadow-lg',
        dark: 'bg-slate-900 text-white hover:bg-slate-800',
        outline: 'border border-gray-200 bg-white text-slate-900 hover:bg-gray-50',
        ghost: 'text-slate-700 hover:bg-gray-100',
      },
      size: {
        sm: 'h-8 px-3.5 text-xs',
        md: 'h-10 px-5',
        lg: 'h-12 px-7 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends Omit<HTMLMotionProps<'button'>, 'ref'>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => (
    <motion.button
      ref={ref}
      type={type}
      whileHover={{ y: -2 }}
      whileTap={{ y: 0, scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';
